import React, { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  GridLayout,
  GridLayoutItem,
} from "@progress/kendo-react-layout";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridPageChangeEvent,
} from "@progress/kendo-react-grid";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import {
  Button,
} from "@progress/kendo-react-buttons";
import { FiEdit } from "react-icons/fi";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { MdDelete } from "react-icons/md";
import { FaEye } from "react-icons/fa";
import IconComponent from "../../components/common/IconComponent";
import { checkAcessRights } from "../../_helper/helper";
import { DELETE_ACCESS, EDIT_ACCESS, PRINT_ACCESS } from "../../_contstants/common";
import { useLocation, useNavigate } from "react-router-dom";
import {
  findAllB2BQuotation,
  deleteB2BPackageQuotation,
  findB2BQuotationHistory,
} from "./services/b2bQuotation.services";
import {
  setB2BQuotationID,
  clearB2BQuotationID,
} from "./b2bQuotationSlice";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";
import { Tooltip } from "@progress/kendo-react-tooltip";
import B2BQuotationFilter from "./B2BQuotationFilter";

const B2BQuotationList: React.FC = () => {
  const gridRef = useRef<any>(null);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const isPrintAccess = checkAcessRights(location.pathname, PRINT_ACCESS);
  // const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  const { B2BQuotationList, B2BQuotationFilterDetails, loading } = useAppSelector((state) => state.b2bQuotation);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);

  useEffect(() => {
    dispatch(
      findAllB2BQuotation({
        ...B2BQuotationFilterDetails,
        limit: pageLimit,
        skip: currentPage * pageLimit,
      })
    );
  }, [dispatch, currentPage, pageLimit, B2BQuotationFilterDetails]);



  const pageChange = (event: GridPageChangeEvent) => {
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(event.page.skip / event.page.take));
  };

  const handleEdit = (item: any) => {
    navigate("/b2bquotation/edit", {
      state: { QuotationID: item?.QuotationID || item?.ID },
    });
  };

  const handleView = (item: any) => {
    window.open(`/b2bquotation/view/${item?.QuotationID || item?.ID}`, "_blank");
  };

  const handleOpenDeleteDialog = (ID: number) => {
    dispatch(setB2BQuotationID(ID));
    dispatch(openDialog("deleteB2BQuotation"));
  };

  const handleOpenHistory = (ID: number) => {
    dispatch(findB2BQuotationHistory(ID));
    dispatch(openDialog("b2bQuotationHistory"));
  };

  const HeaderCustomCell = (props: any) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className="table-header"
    >
      {props.children}
    </HeaderThElement>
  );

  const MyDataCustomCell = (props: any) => (
    <td {...props.tdProps} colSpan={1} className="table-cell">
      {props.children}
    </td>
  );

  const MyEditCommandCell = (props: any) => {
    const item = props.dataItem;
    return (
      <td {...props.tdProps} className="action-td table-cell overflow-visible">
        {isPrintAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="View"
            onClick={() => handleView(item)}
            icon={FaEye}
            iconStyle={{ fontSize: "20px", color: "#0891b2" }}
          />
        )}
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            onClick={() => handleEdit(item)}
            icon={FiEdit}
            iconStyle={{ fontSize: "20px" }}
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            className="color-red"
            onClick={() => handleOpenDeleteDialog(item?.QuotationID || item?.ID)}
            icon={MdDelete}
            iconStyle={{ fontSize: "24px" }}
          />
        )}
      </td>
    );
  };

  return (
    <div className="p-3">
      <DeleteB2BQuotationDialog
        onSuccess={() => {
          dispatch(findAllB2BQuotation({ ...B2BQuotationFilterDetails, limit: pageLimit, skip: currentPage * pageLimit }));
        }}
      />
      <B2BQuotationHistoryDialog />

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <Typography.h4 className="fw-bold m-0">B2B Quotations</Typography.h4>
        </div>
        {/* <Button
          themeColor="primary"
          fillMode="solid"
          onClick={() => navigate("/b2bquotation/create")}
        >
          <IconComponent icon={FaPlus} className="me-2" /> Create New
        </Button> */}
      </div>

      {/* ✅ B2B Filter Panel */}
      <div className="mb-3">
        <B2BQuotationFilter />
      </div>

      <div className="card border-0 shadow-sm rounded-0">
        {loading && <LoadingPanel gridRef={gridRef} />}
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 265px)" }}
          className="responsive-table"
          filterable={false}
          data={B2BQuotationList}
          total={(B2BQuotationList[0] as any)?.TotalCount || B2BQuotationList.length}
          skip={currentPage * pageLimit}
          take={pageLimit}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 50, 100, 500],
            pageSizeValue: pageLimit,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="QuotationNo"
            title="Quotation No."
            width={150}
            cells={{
              data: (props: any) => (
                <td>
                  <div className="d-flex gap-2 align-items-center">
                    <div className="hoverableName" onClick={() => (isPrintAccess ? handleView(props.dataItem) : null)}>
                      {props.dataItem.QuotationNo}
                    </div>
                    {props.dataItem?.IsQuotationHasChild && isPrintAccess && (
                      <Tooltip anchorElement="target" position="top" parentTitle={true}>
                        <span onClick={() => handleOpenHistory(props.dataItem?.ParentID || props.dataItem?.ID)} style={{ cursor: "pointer" }}>
                          <IconComponent
                            icon={FaEye}
                            title="View History"
                            style={{
                              color: "green",
                              fontSize: 18,
                            }}
                          />
                        </span>
                      </Tooltip>
                    )}
                  </div>
                </td>
              ),
            }}
          />
          <Column
            field="CreatedOn"
            title="Date"
            width={120}
            cells={{
              data: (props: any) => <td>{props.dataItem.CreatedOn}</td>,
            }}
          />
          <Column
            field="FirstName"
            title="Customer Name"
            width={200}
            cells={{
              data: (props: any) => {
                const name = `${props.dataItem.FirstName || ""} ${props.dataItem.LastName || ""}`.trim();
                return <td>{name || "-"}</td>;
              },
            }}
          />
          <Column
            field="MobileNo"
            title="Mobile No."
            width={150}
            cells={{
              data: (props: any) => <td>{props.dataItem.MobileNo || "-"}</td>,
            }}
          />
          <Column
            field="EmailID"
            title="Email ID"
            width={200}
            cells={{
              data: (props: any) => <td>{props.dataItem.EmailID || "-"}</td>,
            }}
          />
          <Column
            field="CreatedName"
            title="Created By"
            width={180}
            cells={{
              data: (props: any) => <td>{props.dataItem.CreatedName || "-"}</td>,
            }}
          />
          <Column
            cells={{ data: MyEditCommandCell }}
            title="Actions"
            width={150}
            locked
          />
        </KendoGrid>
      </div>
    </div>
  );
};

const DeleteB2BQuotationDialog: React.FC<{ onSuccess: () => void }> = ({ onSuccess }) => {
  const dispatch = useAppDispatch();
  const { B2BQuotationID } = useAppSelector((state) => state.b2bQuotation);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);

  const handleDelete = async () => {
    if (B2BQuotationID) {
      const res = await dispatch(deleteB2BPackageQuotation(B2BQuotationID));
      if (res.meta.requestStatus === "fulfilled") {
        dispatch(closeDialog());
        dispatch(clearB2BQuotationID());
        onSuccess();
      }
    }
  };

  return dialogName === "deleteB2BQuotation" ? (
    <AppDialog>
      <>
        <Typography.h5>{"Delete Quotation"}</Typography.h5>
        <GridLayout>
          <GridLayoutItem>
            <Typography.p>
              Are you sure you want to delete this Quotation?
            </Typography.p>
          </GridLayoutItem>
          <GridLayoutItem
            style={{
              display: "flex",
              justifyContent: "end",
            }}
          >
            <DialogCloseButton themeColor="error" />
            <Button
              fillMode={"solid"}
              themeColor={"error"}
              onClick={handleDelete}
              type="button"
            >
              Delete
            </Button>
          </GridLayoutItem>
        </GridLayout>
      </>
    </AppDialog>
  ) : null;
};

const B2BQuotationHistoryDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const gridRef = useRef<any>(null);
  const { B2BQuotationHistory, loading } = useAppSelector((state) => state.b2bQuotation);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const location = useLocation();

  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const isPrintAccess = checkAcessRights(location.pathname, PRINT_ACCESS);

  const handleView = (item: any) => {
    window.open(`/b2bquotation/view/${item?.ID || item?.QuotationID}`, "_blank");
  };

  const handleEdit = (item: any) => {
    dispatch(closeDialog());
    navigate("/b2bquotation/edit", {
      state: { QuotationID: item?.ID || item?.QuotationID },
    });
  };

  const handleOpenDeleteDialog = (ID: number) => {
    dispatch(setB2BQuotationID(ID));
    dispatch(openDialog("deleteB2BQuotation"));
  };

  const HistoryActionCell = ({ item }: { item: any }) => {
    return (
      <div className="d-flex gap-2">
        {isPrintAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="View"
            onClick={() => handleView(item)}
            icon={FaEye}
            iconStyle={{ fontSize: "18px" }}
          />
        )}
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            onClick={() => handleEdit(item)}
            icon={FiEdit}
            iconStyle={{ fontSize: "18px" }}
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            className="color-red"
            onClick={() => handleOpenDeleteDialog(item?.ID || item?.QuotationID)}
            icon={MdDelete}
            iconStyle={{ fontSize: "20px" }}
          />
        )}
      </div>
    );
  };

  return dialogName === "b2bQuotationHistory" ? (
    <AppDialog>
      <div className="p-2" style={{ minWidth: "900px", position: "relative" }}>
        <Typography.h5 className="fw-bold mb-3">Quotation History</Typography.h5>
        {loading && <LoadingPanel gridRef={gridRef} />}

        <div style={{ maxHeight: "calc(100vh - 200px)", overflow: "auto" }}>
          <div className="table-responsive">
            <table className="table table-bordered mb-0">
              <thead>
                <tr style={{ whiteSpace: "nowrap" }}>
                  <th style={{ background: "black", color: "white" }}>Quotation No</th>
                  <th style={{ background: "black", color: "white" }}>Date</th>
                  <th style={{ background: "black", color: "white", minWidth: "150px" }}>Name</th>
                  <th style={{ background: "black", color: "white" }}>Mobile No.</th>
                  <th style={{ background: "black", color: "white" }}>Email ID</th>
                  <th style={{ background: "black", color: "white" }}>Created By</th>
                  <th style={{ background: "black", color: "white", minWidth: "100px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {B2BQuotationHistory && B2BQuotationHistory.length > 0 ? (
                  B2BQuotationHistory.map((h: any, i: number) => (
                    <tr key={i}>
                      <td>
                        <div
                          className="hoverableName"
                          onClick={() => handleView(h)}
                        >
                          {h.QuotationNo}
                        </div>
                      </td>
                      <td>{h.CreatedOn}</td>
                      <td>
                        <div className="fw-600">
                          {`${h.FirstName || ""} ${h.LastName || ""}`.trim() || "-"}
                        </div>
                      </td>
                      <td>{h.MobileNo || "-"}</td>
                      <td>{h.EmailID || "-"}</td>
                      <td>{h.CreatedName || "-"}</td>
                      <td>
                        <HistoryActionCell item={h} />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center p-3 text-muted">No history found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="d-flex justify-content-end mt-3">
          <DialogCloseButton />
        </div>
      </div>
    </AppDialog>
  ) : null;
};

export default B2BQuotationList;
